import { IconShieldOff, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type {
  Category,
  RedactionNotice,
} from "../../../domains/assistant/redaction";
import { cn } from "../../../lib/utils";

export type PatientDataBannerContent =
  | { kind: "inputRule"; onDismiss: () => void }
  | { kind: "notice"; notice: RedactionNotice; onDismiss: () => void }
  // The landing only: the composer keeps the text, and its Send is the retry (OD-04).
  | { kind: "checkFailed" };

const FRAME = cn(
  "flex w-full items-start gap-2 rounded-xl bg-ds-black/4 py-2.5 pr-2  pl-3",
  "font-dm-sans text-footnote text-ds-slate-700"
);

/**
 * The top of an assistant composer (AD-18): the Input rule until the first
 * send of this browser, then the Notice of the last send.
 */
export function PatientDataBanner({
  content,
}: {
  content: PatientDataBannerContent;
}): React.JSX.Element {
  const { t } = useTranslation();

  switch (content.kind) {
    case "inputRule":
      return (
        <DismissibleFrame role="note" onDismiss={content.onDismiss}>
          <span className="font-semibold text-ds-slate-900">
            {t("Describe the case, not the patient.")}
          </span>{" "}
          {t(
            "Juisci masks the patient names, addresses, and hospital and national identifiers it detects, but it can miss some."
          )}
        </DismissibleFrame>
      );
    case "notice":
      return (
        <DismissibleFrame role="status" onDismiss={content.onDismiss}>
          <NoticeSentence notice={content.notice} />
        </DismissibleFrame>
      );
    case "checkFailed":
      return (
        <div role="alert" className={FRAME}>
          <IconShieldOff
            className="mt-0.5 size-4 shrink-0 text-ds-clementine-700"
            stroke={1.5}
          />
          <p className="my-0">
            {t("Message not sent: the patient data check failed.")}
          </p>
        </div>
      );
    default:
      throw new Error(content satisfies never);
  }
}

function DismissibleFrame({
  role,
  onDismiss,
  children,
}: {
  role: "note" | "status";
  onDismiss: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div role={role} className={FRAME}>
      <p className="my-0 flex-1">{children}</p>
      {/* A 44px target around the 18px cross, pulled into the padding so the
          banner keeps the design's size. */}
      <button
        type="button"
        aria-label={t("Close")}
        className="-my-3 -mr-3 -ml-2 flex size-11 shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0"
        onClick={onDismiss}
      >
        <IconX className="size-4.5 text-ds-slate-600" stroke={2} />
      </button>
    </div>
  );
}

function NoticeSentence({
  notice,
}: {
  notice: RedactionNotice;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();

  function categoryLabel(category: Category): string {
    switch (category) {
      case "name_with_birth_date":
        return t("name and date of birth");
      case "patient_name":
        return t("name");
      case "contact":
        return t("contact details");
      case "hospital_identifier":
        return t("hospital identifier");
      case "address":
        return t("address");
      case "national_identifier":
        return t("national identifier");
      default:
        throw new Error(category satisfies never);
    }
  }

  const categories = new Intl.ListFormat(i18n.language, {
    type: "conjunction",
  }).format(notice.summary.categories.map(categoryLabel));
  // One sentence, one key: the categories are styled where the translation puts them.
  const sentence = t(
    "{{count}} patient details masked before sending: {{categories}}.",
    { count: notice.summary.matchCount, categories }
  );
  const at = sentence.indexOf(categories);
  if (at === -1)
    throw new Error("should not happen: the Notice lost its categories");

  return (
    <>
      {sentence.slice(0, at)}
      <span className="font-medium text-ds-slate-900">{categories}</span>
      {sentence.slice(at + categories.length)}
    </>
  );
}
